import { useEffect, useRef, useState } from 'react';
import trophySrc from './assets/cert-trophy.jpg';
import logoDarkSrc from './assets/logo-on-dark.png';

/** What a certificate says. The design follows CK's payout certificate. */
export interface CertificateData {
  /** First line of the big title, e.g. "Payout", "Phase 1", "Funded", "Welcome". The second line is always "Certificate". */
  headline: string;
  name: string;
  /** Second box, e.g. "Paid amount" / "$1,753" or "Account size" / "$100,000". */
  label?: string;
  value?: string;
  date: string;
  signatory: string;
  signatoryTitle: string;
  /** Short reference printed small, so anyone can check a certificate with the firm. */
  id?: string;
  /** A firm's own logo (white-label). CK uses its built-in logo. */
  logoUrl?: string | null;
  accent?: string;
}

const SIZE = 1080;
const cache = new Map<string, Promise<HTMLImageElement>>();
function img(src: string) {
  if (!cache.has(src)) {
    cache.set(src, new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('image'));
      i.src = src;
    }));
  }
  return cache.get(src)!;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Shrinks text until it fits the given width. */
function fit(ctx: CanvasRenderingContext2D, text: string, weight: number, size: number, maxW: number, family: string) {
  let s = size;
  do { ctx.font = `${weight} ${s}px ${family}`; s -= 2; } while (ctx.measureText(text).width > maxW && s > 18);
}

/** Draws the certificate on a square canvas (1080 × 1080, the size social networks use). */
export async function drawCertificate(canvas: HTMLCanvasElement, d: CertificateData, scale = 2) {
  const W = SIZE, H = SIZE;
  canvas.width = W * scale; canvas.height = H * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  const family = "'Archivo Variable', 'Archivo', system-ui, sans-serif";
  try { await Promise.all([document.fonts.load(`800 40px ${family}`), document.fonts.load(`600 40px ${family}`)]); } catch { /* fallback font */ }
  const accent = d.accent ?? '#FF8A00';

  // Background: black with a warm glow, like the payout certificate.
  ctx.fillStyle = '#050505'; ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(W * 0.05, H * 0.55, 0, W * 0.05, H * 0.55, W * 0.75);
  g1.addColorStop(0, 'rgba(255,138,0,0.22)'); g1.addColorStop(1, 'rgba(255,138,0,0)');
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(W, 0, 0, W, 0, W * 0.6);
  g2.addColorStop(0, 'rgba(255,138,0,0.18)'); g2.addColorStop(1, 'rgba(255,138,0,0)');
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);

  // Trophy on the right, fading into the background on its left edge.
  try {
    const t = await img(trophySrc);
    const th = H * 0.8, tw = th * (t.width / t.height), tx = W - tw, ty = H - th;
    ctx.drawImage(t, tx, ty, tw, th);
    const fade = ctx.createLinearGradient(tx, 0, tx + tw * 0.28, 0);
    fade.addColorStop(0, 'rgba(5,5,5,1)'); fade.addColorStop(1, 'rgba(5,5,5,0)');
    ctx.fillStyle = fade; ctx.fillRect(tx - 1, ty, tw * 0.28 + 1, th);
    const top = ctx.createLinearGradient(0, ty, 0, ty + 80);
    top.addColorStop(0, 'rgba(5,5,5,1)'); top.addColorStop(1, 'rgba(5,5,5,0)');
    ctx.fillStyle = top; ctx.fillRect(tx, ty - 1, tw, 81);
  } catch { /* trophy optional */ }

  // Logo.
  try {
    const l = await img(d.logoUrl ?? logoDarkSrc);
    const lh = 64, lw = lh * (l.width / l.height);
    ctx.drawImage(l, 84, 78, Math.min(lw, 360), lh * Math.min(1, 360 / lw));
  } catch { /* logo optional */ }

  const left = 84;
  // Title.
  ctx.fillStyle = accent; ctx.textBaseline = 'alphabetic';
  fit(ctx, d.headline, 800, 104, 560, family); ctx.fillText(d.headline, left, 300);
  ctx.font = `800 104px ${family}`; fit(ctx, 'Certificate', 800, 104, 560, family); ctx.fillText('Certificate', left, 410);
  ctx.font = `600 34px ${family}`; ctx.fillText('Awarded to', left, 472);

  // Name box.
  const box = (y: number, w: number, h: number) => {
    roundRect(ctx, left, y, w, h, 22);
    const bg = ctx.createLinearGradient(left, y, left + w, y + h);
    bg.addColorStop(0, 'rgba(60,36,12,0.9)'); bg.addColorStop(1, 'rgba(20,14,8,0.85)');
    ctx.fillStyle = bg; ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 1.5; ctx.stroke();
  };
  box(500, 510, 138);
  ctx.fillStyle = '#F4EFE6'; fit(ctx, d.name, 700, 64, 450, family); ctx.fillText(d.name, left + 34, 591);

  // Value box.
  if (d.label && d.value) {
    ctx.fillStyle = '#F4EFE6'; ctx.font = `600 34px ${family}`; ctx.fillText(d.label, left, 712);
    box(740, 460, 138);
    ctx.fillStyle = '#F4EFE6'; fit(ctx, d.value, 800, 80, 390, family); ctx.fillText(d.value, left + 40, 838);
  }

  // Date and signature.
  ctx.fillStyle = '#F4EFE6'; ctx.font = `600 27px ${family}`;
  ctx.fillText(d.date, left + 26, 958); ctx.fillText(d.signatory, left + 290, 958);
  ctx.fillStyle = 'rgba(244,239,230,0.75)'; ctx.font = `500 21px ${family}`;
  ctx.fillText('Date', left + 26, 994); ctx.fillText(d.signatoryTitle, left + 290, 994);
  if (d.id) { ctx.textAlign = 'right'; ctx.fillStyle = 'rgba(244,239,230,0.4)'; ctx.font = `500 16px ${family}`; ctx.fillText(`ID ${d.id}`, W - 30, H - 26); ctx.textAlign = 'left'; }
}

/** The certificate as an image, ready to show, download or print. */
export function CertificateImage({ data, className }: { data: CertificateData; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const key = JSON.stringify(data);
  const latest = useRef(key);
  useEffect(() => {
    latest.current = key;
    const c = document.createElement('canvas');
    drawCertificate(c, data, 1.5).then(() => { if (latest.current === key) setSrc(c.toDataURL('image/jpeg', 0.92)); }).catch(() => setSrc(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return src ? <img className={className ?? 'cert-img'} src={src} alt={`${data.headline} certificate for ${data.name}`} /> : <div className={`${className ?? 'cert-img'} cert-loading`} aria-busy="true" />;
}

/** Saves the certificate as a high-resolution PNG. */
export async function downloadCertificate(data: CertificateData, fileName: string) {
  const c = document.createElement('canvas');
  await drawCertificate(c, data, 2);
  const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/png'));
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${fileName}.png`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Opens a print view so the certificate can be saved as a PDF. */
export async function printCertificate(data: CertificateData) {
  const c = document.createElement('canvas');
  await drawCertificate(c, data, 2);
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><title>${data.headline} certificate</title><style>@page{size:auto;margin:0}html,body{margin:0;background:#050505}img{display:block;width:100vmin;height:100vmin;margin:0 auto}</style><img src="${c.toDataURL('image/png')}"><script>window.onload=()=>setTimeout(()=>print(),200)<\/script>`);
  w.document.close();
}

export const formatCertDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
